import React, { useEffect, useState, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  InputAdornment,
  TextField,
  CircularProgress,
  Paper
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PersonIcon from '@mui/icons-material/Person'
import { subscribePlaceOrder, orderCount } from '../../apollo'
import paymentLabel from '../../utils/paymentLabel'
import { dateIn, orderZone, timeIn } from '../../utils/restaurantTime'
import { orderTotal } from './OrderCard'

const ORDERCOUNT = gql`${orderCount}`
const ORDER_PLACED = gql`${subscribePlaceOrder}`

const STATUS_CONFIG = {
  PENDING:   { label: 'New',        color: '#d97706', bg: '#fef3c7' },
  ACCEPTED:  { label: 'Accepted',   color: '#2563eb', bg: '#eff6ff' },
  PREPARED:  { label: 'Ready',      color: '#7c3aed', bg: '#f5f3ff' },
  ASSIGNED:  { label: 'Rider Out',  color: '#0891b2', bg: '#ecfeff' },
  PICKED:    { label: 'On The Way', color: '#4f46e5', bg: '#eef2ff' },
  DELIVERED: { label: 'Delivered',  color: '#16a34a', bg: '#f0fdf4' },
  CANCELLED: { label: 'Cancelled',  color: '#dc2626', bg: '#fef2f2' },
}

const STATUS_TABS = [
  { value: 'ALL',       label: 'All' },
  { value: 'PENDING',   label: 'New' },
  { value: 'ACCEPTED',  label: 'Accepted' },
  { value: 'PREPARED',  label: 'Ready' },
  { value: 'ASSIGNED',  label: 'Rider Out' },
  { value: 'PICKED',    label: 'On The Way' },
  { value: 'DELIVERED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
]

const DATE_FILTERS = [
  { value: 'ALL',        label: 'All Time' },
  { value: 'TODAY',      label: 'Today' },
  { value: 'YESTERDAY',  label: 'Yesterday' },
  { value: 'LAST_WEEK',  label: 'Last Week' },
  { value: 'THIS_MONTH', label: 'This Month' },
]

const filterByDate = (orders, dateFilter) => {
  if (dateFilter === 'ALL') return orders
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const lastWeek = new Date(today.getTime() - 7 * 86400000)
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  return orders.filter(o => {
    const d = new Date(o.createdAt)
    switch (dateFilter) {
      case 'TODAY':      return d >= today
      case 'YESTERDAY':  return d >= yesterday && d < today
      case 'LAST_WEEK':  return d >= lastWeek
      case 'THIS_MONTH': return d >= thisMonth
      default:           return true
    }
  })
}

const getStatusLabel = row => {
  if (row.isPickedUp) {
    if (row.orderStatus === 'PICKED')    return { ...STATUS_CONFIG.PICKED,    label: 'Ready Pickup' }
    if (row.orderStatus === 'DELIVERED') return { ...STATUS_CONFIG.DELIVERED, label: 'Picked Up' }
  }
  return STATUS_CONFIG[row.orderStatus] || { label: row.orderStatus, color: '#6b7280', bg: '#f3f4f6' }
}

const getItems = items =>
  (items || [])
    .map(i => `${i.quantity}x ${i.title}${i.variation?.title ? ` (${i.variation.title})` : ''}`)
    .join(', ')

const OrdersData = props => {
  const { selected, updateSelected, toggleModal, isSuperAdmin } = props
  const restaurantId = localStorage.getItem('restaurantId')

  const [searchQuery, setSearchQuery]   = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [dateFilter, setDateFilter]     = useState('ALL')
  const [page, setPage]                 = useState(0)
  const [rowsPerPage, setRowsPerPage]   = useState(10)

  const { data: countData } = useQuery(ORDERCOUNT, {
    variables: { restaurant: restaurantId }
  })

  useEffect(() => {
    if (isSuperAdmin || !restaurantId) return
    props.subscribeToMore({
      document: ORDER_PLACED,
      variables: { restaurant: restaurantId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev
        const newOrder = subscriptionData.data.subscribePlaceOrder
        if (!newOrder) return prev
        const existingIndex = (prev.ordersByRestId || []).findIndex(o => o._id === newOrder._id)
        if (existingIndex === -1) {
          return { ordersByRestId: [newOrder, ...(prev.ordersByRestId || [])] }
        }
        const updated = [...prev.ordersByRestId]
        updated[existingIndex] = newOrder
        return { ordersByRestId: updated }
      },
      onError: err => console.log('subscription error', err)
    })
  }, []) // run once on mount

  useEffect(() => {
    if (selected && props.orders) {
      const order = props.orders.find(o => o._id === selected._id)
      if (order) updateSelected(order)
    }
  }, [props.orders])

  // Reset page when filters change
  useEffect(() => { setPage(0) }, [searchQuery, statusFilter, dateFilter])

  const filtered = useMemo(() => {
    let result = props.orders || []
    if (dateFilter !== 'ALL') result = filterByDate(result, dateFilter)
    if (statusFilter !== 'ALL') result = result.filter(o => o.orderStatus === statusFilter)
    if (searchQuery.length > 1) {
      const re = new RegExp(searchQuery, 'i')
      result = result.filter(o =>
        re.test(o.orderId) || re.test(o.user?.name || '') || re.test(o.orderStatus)
      )
    }
    return result
  }, [props.orders, dateFilter, statusFilter, searchQuery])

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
    props.page && props.page(newPage + 1)
  }

  const handleChangeRowsPerPage = e => {
    const rpp = parseInt(e.target.value, 10)
    setRowsPerPage(rpp)
    setPage(0)
    props.rows && props.rows(rpp)
    props.page && props.page(1)
  }

  if (props.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={10}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Filters row */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="flex-start">
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search order ID, customer..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9ca3af', fontSize: 18 }} />
              </InputAdornment>
            )
          }}
          sx={{
            width: { xs: '100%', sm: 260 },
            '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem', bgcolor: 'white' }
          }}
        />

        {/* Date filter chips */}
        <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
          {DATE_FILTERS.map(df => (
            <Chip
              key={df.value}
              label={df.label}
              size="small"
              onClick={() => setDateFilter(df.value)}
              sx={{
                cursor: 'pointer',
                fontWeight: dateFilter === df.value ? 700 : 500,
                fontSize: '0.72rem',
                bgcolor: dateFilter === df.value ? '#111827' : 'white',
                color: dateFilter === df.value ? 'white' : '#374151',
                border: '1.5px solid #d1d5db',
                '&:hover': { bgcolor: '#374151', color: 'white' }
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Status filter chips */}
      <Box display="flex" flexWrap="wrap" gap={0.75} mb={2}>
        {STATUS_TABS.map(tab => {
          const cfg = STATUS_CONFIG[tab.value]
          const isActive = statusFilter === tab.value
          const color = cfg?.color || '#374151'
          return (
            <Chip
              key={tab.value}
              label={tab.label}
              size="small"
              onClick={() => setStatusFilter(tab.value)}
              sx={{
                cursor: 'pointer',
                fontWeight: isActive ? 700 : 500,
                fontSize: '0.72rem',
                bgcolor: isActive ? color : 'white',
                color: isActive ? 'white' : (color || '#374151'),
                border: `1.5px solid ${color || '#d1d5db'}`,
                '&:hover': { bgcolor: color, color: 'white' }
              }}
            />
          )
        })}
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f9fafb' }}>
              {['Order ID', 'Customer', 'Items', 'Total', 'Payment', 'Type', 'Status', 'Date'].map(h => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 1.5,
                    borderBottom: '1px solid #e5e7eb',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#9ca3af' }}>
                  No orders found
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((order, idx) => {
                const statusCfg = getStatusLabel(order)
                const isActive = order.orderStatus !== 'DELIVERED' && order.orderStatus !== 'CANCELLED'
                const total = orderTotal(order).toFixed(2)

                return (
                  <TableRow
                    key={order._id}
                    onClick={() => toggleModal(order)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isActive
                        ? idx % 2 === 0 ? '#fffbeb' : '#fef9e7'
                        : idx % 2 === 0 ? 'white' : '#fafafa',
                      '&:hover': { bgcolor: '#f0f9ff' },
                      transition: 'background 0.1s',
                      '& td': { borderBottom: '1px solid #f3f4f6' }
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#111827', fontSize: '0.8rem' }}>
                        #{order.orderId}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <Box sx={{
                          width: 26, height: 26, borderRadius: '50%',
                          bgcolor: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <PersonIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: '#111827', lineHeight: 1.2 }}>
                            {order.user?.name || '—'}
                          </Typography>
                          {order.user?.phone && (
                            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                              {order.user.phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, maxWidth: 200 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: '0.78rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}
                        title={getItems(order.items)}
                      >
                        {getItems(order.items)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: '#111827' }}>
                        ${total}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#6b7280' }}>
                        {paymentLabel(order)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={order.isPickedUp ? 'Pickup' : 'Delivery'}
                        size="small"
                        sx={{
                          fontSize: '0.68rem', fontWeight: 600, height: 20,
                          bgcolor: order.isPickedUp ? '#ecfeff' : '#f0fdf4',
                          color: order.isPickedUp ? '#0891b2' : '#16a34a',
                          border: `1px solid ${order.isPickedUp ? '#a5f3fc' : '#bbf7d0'}`
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={statusCfg.label}
                        size="small"
                        sx={{
                          fontSize: '0.68rem', fontWeight: 700, height: 20,
                          bgcolor: statusCfg.bg, color: statusCfg.color,
                          border: `1px solid ${statusCfg.color}30`
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#374151' }}>
                        {dateIn(order.createdAt, orderZone(order))}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#6b7280' }}>
                        {timeIn(order.createdAt, orderZone(order))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        sx={{
          borderTop: '1px solid #e5e7eb',
          '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
            color: '#374151',
            fontSize: '0.8rem'
          },
          '& .MuiTablePagination-select': { color: '#374151' },
          '& .MuiTablePagination-actions button': { color: '#374151' }
        }}
      />
    </Box>
  )
}

export default withTranslation()(OrdersData)
